/// <amd-module name="Analytics/Boards/BoardsQueries/WitFieldUtilities" />
import { FieldFilter, WiqlOperators, WitFieldType, WorkItemFieldDescriptor } from "./DataContracts";

/**
 * Utility class for working with work item types in analytics queries.
 */
export class WitFieldUtilities {
    // Analytics uses the following mapping from common property names to what they are named on the WorkItem entity
    // https://mseng.visualstudio.com/DefaultCollection/VSOnline/_git/VSO?path=%2FAnalytics%2FService%2FServer%2FModel%2FWorkItemPrimitive.cs&version=GBmaster&_a=contents
    private static readonly workItemPrimitiveCommonPropertiesMapping: { [key: string]: string } = {
        "System.Id": "WorkItemId",
        "System.Rev": "Revision",
        "System.Watermark": "Watermark",
        "System.Title": "Title",
        "System.WorkItemType": "WorkItemType",
        "System.ChangedDate": "ChangedDate",
        "System.CreatedDate": "CreatedDate",
        "System.State": "State",
        "System.Reason": "Reason",
        "System.IsDeleted": "IsDeleted",
        "System.Tags": "TagNames",
        "Microsoft.VSTS.Common.StateChangeDate": "StateChangeDate",
        "System.StateChangeDate": "StateChangeDate",
        "Microsoft.VSTS.Build.FoundIn": "FoundIn",
        "Microsoft.VSTS.Build.IntegrationBuild": "IntegrationBuild",
        "Microsoft.VSTS.Common.Activity": "Activity",
        "Microsoft.VSTS.Common.ActivatedDate": "ActivatedDate",
        "Microsoft.VSTS.Common.BacklogPriority": "BacklogPriority",
        "Microsoft.VSTS.Common.BusinessValue": "BusinessValue",
        "Microsoft.VSTS.Common.ClosedDate": "ClosedDate",
        "Microsoft.VSTS.Common.Discipline": "Discipline",
        "Microsoft.VSTS.Common.Issue": "Issue",
        "Microsoft.VSTS.Common.Priority": "Priority",
        "Microsoft.VSTS.Common.Rating": "Rating",
        "Microsoft.VSTS.Common.ResolvedDate": "ResolvedDate",
        "Microsoft.VSTS.Common.ResolvedReason": "ResolvedReason",
        "Microsoft.VSTS.Common.Risk": "Risk",
        "Microsoft.VSTS.Common.Severity": "Severity",
        "Microsoft.VSTS.Common.StackRank": "StackRank",
        "Microsoft.VSTS.Common.TimeCriticality": "TimeCriticality",
        "Microsoft.VSTS.Common.Triage": "Triage",
        "Microsoft.VSTS.Common.ValueArea": "ValueArea",
        "Microsoft.VSTS.Scheduling.CompletedWork": "CompletedWork",
        "Microsoft.VSTS.Scheduling.DueDate": "DueDate",
        "Microsoft.VSTS.Scheduling.Effort": "Effort",
        "Microsoft.VSTS.Scheduling.FinishDate": "FinishDate",
        "Microsoft.VSTS.Scheduling.OriginalEstimate": "OriginalEstimate",
        "Microsoft.VSTS.Scheduling.RemainingWork": "RemainingWork",
        "Microsoft.VSTS.Scheduling.Size": "Size",
        "Microsoft.VSTS.Scheduling.StartDate": "StartDate",
        "Microsoft.VSTS.Scheduling.StoryPoints": "StoryPoints",
        "Microsoft.VSTS.Scheduling.TargetDate": "TargetDate",
        "Microsoft.VSTS.CMMI.Blocked": "Blocked",
        "Microsoft.VSTS.CMMI.Committed": "Committed",
        "Microsoft.VSTS.CMMI.Escalate": "Escalate",
        "Microsoft.VSTS.CMMI.FoundInEnvironment": "FoundInEnvironment",
        "Microsoft.VSTS.CMMI.HowFound": "HowFound",
        "Microsoft.VSTS.CMMI.Probability": "Probability",
        "Microsoft.VSTS.CMMI.RequirementType": "RequirementType",
        "Microsoft.VSTS.CMMI.RequiresReview": "RequiresReview",
        "Microsoft.VSTS.CMMI.RequiresTest": "RequiresTest",
        "Microsoft.VSTS.CMMI.RootCause": "RootCause",
        "Microsoft.VSTS.CMMI.SubjectMatterExpert1": "SubjectMatterExpert1",
        "Microsoft.VSTS.CMMI.SubjectMatterExpert2": "SubjectMatterExpert2",
        "Microsoft.VSTS.CMMI.SubjectMatterExpert3": "SubjectMatterExpert3",
        "Microsoft.VSTS.CMMI.TargetResolveDate": "TargetResolveDate",
        "Microsoft.VSTS.CMMI.TaskType": "TaskType",
        "Microsoft.VSTS.CMMI.UserAcceptanceTest": "UserAcceptanceTest",
        "Microsoft.VSTS.TCM.AutomatedTestId": "AutomatedTestId",
        "Microsoft.VSTS.TCM.AutomatedTestName": "AutomatedTestName",
        "Microsoft.VSTS.TCM.AutomatedTestStorage": "AutomatedTestStorage",
        "Microsoft.VSTS.TCM.AutomatedTestType": "AutomatedTestType",
        "Microsoft.VSTS.TCM.AutomationStatus": "AutomationStatus"
    };
    public static readonly tagsReferenceName = "System.Tags";

    /**
     * Replaces unsupported characters with an alternative representation.
     *
     * - '.' replaced with '_'
     * - Non-alphanumeric/underscore characters are replaced with their unicode value in the format "__####"
     *     - #### matches C#'s "ToString("X4")" format
     *
     * Reference: https://mseng.visualstudio.com/VSOnline/Atlas/_git/VSO?path=%2FAnalytics%2FService%2FServer%2FOData%2FInfrastructure%2FProcessFieldInfo.cs
     * @param input The string to replace
     * @returns The input string with unsupported characters replaced
     */
    private static replaceUnsupportedCharacters(input: string): string {
        const accumulator: string[] = [];
        for (let i = 0; i < input.length; ++i) {
            const char = input[i];
            if (char === ".") {
                accumulator.push("_");
            } else if (/[^\w\s\.]/.test(char)) {
                // Convert to hex with uppercase letters
                let hexVal = char
                    .charCodeAt(0)
                    .toString(16)
                    .toUpperCase();

                // Pad with prefix'd 0's
                hexVal = "0000" + hexVal;
                hexVal = hexVal.slice(-4);

                accumulator.push(`__${hexVal}`);
            } else {
                accumulator.push(char);
            }
        }

        return accumulator.join("");
    }

    private static tagOperatorToODataClause(tagOperator: string): string {
        switch (tagOperator.toLowerCase().trim()) {
            case "Contains".toLowerCase():
                return "eq";
            case "Does not contain".toLowerCase():
                return "ne"; // This format is used with our initial field picker
            case "Not contains".toLowerCase():
                return "ne"; // TODO: Rationalize format representations This is the format coming from rich Field picker
        }
        return "";
    }

    private static getTagsClause(filters: FieldFilter[]): string {
        const eqFilters = [];
        const neFilters = [];
        for (const f of filters) {
            if (f.fieldName === WitFieldUtilities.tagsReferenceName) {
                if (WitFieldUtilities.tagOperatorToODataClause(f.queryOperation) === "eq") {
                    eqFilters.push(`Tags/any(t:t/TagName eq '${WitFieldUtilities.escapeString(f.queryValue)}')`);
                } else {
                    neFilters.push(`t/TagName ne '${WitFieldUtilities.escapeString(f.queryValue)}'`);
                }
            }
        }

        let result = eqFilters.join(" and ");

        if (neFilters.length > 0) {
            if (result != "") {
                result += " and ";
            }

            result += `Tags/all(t:${neFilters.join(" and ")})`;
        }

        return result;
    }

    private static operatorToODataOperator(filterOperator: string): string {
        switch (filterOperator.trim()) {
            case ">":
                return "gt";
            case ">=":
                return "ge";
            case "<":
                return "lt";
            case "<=":
                return "le";
            case "=":
                return "eq";
            case "<>":
                return "ne";
        }
        return "";
    }

    public static getFieldODataPropertyName(fieldReferenceName: string): string {
        // Replace common fields with alternate representation from hardcoded AX list
        const replacement = WitFieldUtilities.workItemPrimitiveCommonPropertiesMapping[fieldReferenceName];
        if (replacement != null) {
            fieldReferenceName = replacement;
        }

        // Fields that start with a number are given an underscore prefix
        // Regex copied from https://mseng.visualstudio.com/VSOnline/Atlas/_git/VSO?path=%2FAnalytics%2FService%2FServer%2FOData%2FInfrastructure%2FProcessFieldInfo.cs
        if (/^(\d+)/.test(fieldReferenceName)) {
            fieldReferenceName = `_${fieldReferenceName}`;
        }

        return WitFieldUtilities.replaceUnsupportedCharacters(fieldReferenceName);
    }

    public static escapeString(value: string): string {
        return value.split("'").join("''"); // Replace all
    }

    public static getFiltersClause(filters: FieldFilter[], workItemFieldDescriptors: WorkItemFieldDescriptor[]): string {
        const nonTagFiltersClauses = filters
            .filter(f => f.fieldName !== WitFieldUtilities.tagsReferenceName)
            .map(filter => {
                const descriptors = workItemFieldDescriptors.filter(d => d.FieldReferenceName == filter.fieldName);
                if (descriptors.length > 0) {
                    const descriptor = descriptors[0];

                    // We don't support identities so filter them out
                    if (descriptor.FieldType == "Identity") {
                        return null;
                    }
                    const analyticsFilterName = descriptor.AnalyticsFilterName;
                    const chosenValue = descriptor.GetAnalyticsQueryValueFunc(filter);
                    const escapedChosenValue = WitFieldUtilities.escapeString(chosenValue);

                    if (descriptor.FieldType === WitFieldType[WitFieldType.TreePath] && filter.queryOperation === WiqlOperators.OperatorUnder) {
                        return `(${analyticsFilterName} eq '${escapedChosenValue}' or startswith(${analyticsFilterName},'${escapedChosenValue}\\'))`;
                    } else if (
                        descriptor.FieldType === WitFieldType[WitFieldType.TreePath] &&
                        filter.queryOperation === WiqlOperators.OperatorNotUnder
                    ) {
                        return `((${analyticsFilterName} ne '${escapedChosenValue}' and not startswith(${analyticsFilterName},'${escapedChosenValue}\\')) or ${analyticsFilterName} eq null)`;
                    } else {
                        const quote = descriptor.UseQuotes ? "'" : ""; // Use or don't use quotes around value, based on descriptor
                        const operator = WitFieldUtilities.operatorToODataOperator(filter.queryOperation);

                        const clause = `${analyticsFilterName} ${operator} ${quote}${escapedChosenValue}${quote}`;
                        if(!escapedChosenValue)
                        {
                            return `${analyticsFilterName} ${operator} null`;
                        }  

                        // If the "ne" operator is chosen, we need to explicitly add an "or equals null" clause because otherwise
                        // items with the field set to null will be filtered out. This is undesireable for a not-equals operation.
                        return operator === "ne" ? `(${clause} or ${analyticsFilterName} eq null)` : clause;
                    }
                } else {
                    console.warn("Query did not recognize field: " + filter.fieldName);
                    return null; // If we don't support this field, filter it out
                }
            })
            .filter(f => !!f) // Filter out the filters that are not defined by our supported types
            .join(" and ");

        const tagFiltersClauses = WitFieldUtilities.getTagsClause(filters);

        if (nonTagFiltersClauses && tagFiltersClauses) {
            return [nonTagFiltersClauses, tagFiltersClauses].join(" and ");
        } else if (nonTagFiltersClauses) {
            return nonTagFiltersClauses;
        } else if (tagFiltersClauses) {
            return tagFiltersClauses;
        } else {
            return "";
        }
    }
}
